import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getMultiFactorResolver, PhoneAuthProvider, PhoneMultiFactorGenerator } from 'firebase/auth';
import { auth } from './firebase';
import SmsCodeForm from '../components/SmsCodeForm';

const cancelled = () => Object.assign(new Error('Sign-in cancelled. Please try again.'), { code: 'auth/cancelled-popup-request' });

function PhoneChallenge({ challenge, finish, cancel }) {
  const hints = challenge.resolver.hints.filter(hint => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
  const [factorUid, setFactorUid] = useState(hints[0]?.uid || '');
  const hint = hints.find(item => item.uid === factorUid);
  return <section role="dialog" aria-label="Two-step verification" className="mfa-container">
    <h3>Two-step verification</h3>
    {!hint ? <>
      <p>This account has no supported SMS factor. Contact your administrator.</p>
      <button type="button" onClick={cancel}>Cancel</button>
    </> : <>
      <label>Verification phone
        <select value={factorUid} onChange={event => setFactorUid(event.target.value)}>
          {hints.map(item => <option key={item.uid} value={item.uid}>{item.displayName || item.phoneNumber || 'Enrolled phone'}</option>)}
        </select>
      </label>
      <SmsCodeForm key={factorUid}
        sendCode={captcha => new PhoneAuthProvider(auth).verifyPhoneNumber({ multiFactorHint: hint, session: challenge.resolver.session }, captcha)}
        verifyCode={async (id, code) => {
          const credential = PhoneAuthProvider.credential(id, code);
          const result = await challenge.resolver.resolveSignIn(PhoneMultiFactorGenerator.assertion(credential));
          finish(result);
        }} onCancel={cancel} />
    </>}
  </section>;
}

// Resume the original caller after MFA so its profile/invitation checks still run.
export function useMfaSignIn() {
  const pending = useRef(null);
  const mounted = useRef(true);
  const [challenge, setChallenge] = useState(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      pending.current?.reject(cancelled());
      pending.current = null;
    };
  }, []);
  const signInWithMfa = useCallback(async action => {
    try { return await action(); }
    catch (error) {
      if (error.code !== 'auth/multi-factor-auth-required') throw error;
      if (!mounted.current || pending.current) throw cancelled();
      const resolver = getMultiFactorResolver(auth, error);
      return new Promise((resolve, reject) => {
        const request = { resolver, resolve, reject };
        pending.current = request;
        setChallenge(request);
      });
    }
  }, []);
  const finish = result => {
    if (pending.current !== challenge) return;
    pending.current = null;
    setChallenge(null);
    challenge.resolve(result);
  };
  const cancel = () => {
    if (pending.current !== challenge) return;
    pending.current = null;
    setChallenge(null);
    challenge.reject(cancelled());
  };
  return {
    signInWithMfa,
    mfaPrompt: challenge ? <PhoneChallenge challenge={challenge} finish={finish} cancel={cancel} /> : null,
  };
}
